/**
 * 3-segment data token: {{data.<provider>.<field>}}. Both provider and field segments allow
 * underscores so future snake_case field paths stay matchable; the field class is kept free of
 * scheme chars (`:`/`/`) so the reply-send sanitizer's identical re-validation can't be tricked
 * into emitting a forbidden scheme. Deliberately NOT matched by the 2-segment system-token regex.
 * Single source of truth imported by reply-send/sanitize-email-html.ts (TOKEN_ATTR_RE/TOKEN_ONLY_RE).
 * Capture groups: [1]=provider, [2]=field.
 */
export const DATA_TOKEN_PATTERN = String.raw `\{\{\s*data\.([a-z0-9_]+)\.([a-zA-Z0-9_]+)\s*\}\}`;
const DATA_TOKEN_RE = new RegExp(DATA_TOKEN_PATTERN, 'g');
const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
export const extractDataTokens = (html) => {
    const out = [];
    const seen = new Set();
    for (const m of html.matchAll(DATA_TOKEN_RE)) {
        const key = `${m[1]}.${m[2]}`;
        if (!seen.has(key)) {
            seen.add(key);
            out.push({ provider: m[1], field: m[2] });
        }
    }
    return out;
};
export const interpolateDataTokens = (html, providerKey, values, opts) => {
    // Default true preserves the existing HTML-body behaviour. The plain-text
    // SUBJECT header path passes `escapeHtml: false` so a value with `&`/`<` does
    // not leak `&amp;`/`&lt;` into the Subject line (mirrors how the system-token
    // interpolator distinguishes body vs subject).
    const doEscape = opts?.escapeHtml ?? true;
    return html.replace(DATA_TOKEN_RE, (match, provider, field) => {
        if (provider !== providerKey)
            return match;
        const v = values[field];
        if (v === undefined || v === null)
            return match; // gate already guards; leave intact if absent
        return doEscape ? escapeHtml(String(v)) : String(v);
    });
};
export const validateTemplateDataTokens = (body, subject, providerKey, catalog) => {
    const tokens = extractDataTokens(`${body}\n${subject ?? ''}`);
    const errors = [];
    for (const t of tokens) {
        if (!providerKey) {
            errors.push({ code: 'provider_unbound', provider: t.provider, field: t.field });
            continue;
        }
        if (t.provider !== providerKey) {
            errors.push({ code: 'provider_mismatch', provider: t.provider, field: t.field });
            continue;
        }
        const entry = catalog.find((c) => c.key === providerKey);
        if (!entry || !entry.fields.some((f) => f.path === t.field)) {
            errors.push({ code: 'unknown_field', provider: t.provider, field: t.field });
        }
    }
    return errors;
};
//# sourceMappingURL=tokens.js.map